import {ReactElement, useEffect, useMemo, useState} from "react";
import {Property} from "../../../contexts/properties/logic";
import {
    CopyDriversCrossCoaPropertyInput,
    useCopyDriversCrossCoaMutation,
    useGetCrossCoaCopyCantCopyWithDriversLazyQuery,
    VersionType
} from "../../../__generated__/generated_types";
import {Body, Close, Footer, Header, Modal} from "@zendeskgarden/react-modals";
import {CustomZDDropdown} from "../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {Button} from "@zendeskgarden/react-buttons";
import {Dots} from "@zendeskgarden/react-loaders";
import {toast} from "react-toastify";
import * as css from "./styles/CopyDriverCrossCoaModal.module.scss";

export interface CopyDriverCrossCoaModalPropsFixedDestination {
    allProperties: Property[];
    destination: {
        property: Property;
        budgetYear: number;
        version: VersionType.Reforecast | VersionType.Budget;
    }
    onModalClose: () => void;
    title?: string;
}

export function CopyDriverCrossCoaModalFixedDestination(props: CopyDriverCrossCoaModalPropsFixedDestination): ReactElement {
    // Right now this is only being used for acquisition properties, so I'm going to make some assumptions
    // that simplify the code that will need to changed if other types of properties are required.
    //  1. The destination property year and version will always be "property.reforecastYear" an "VersionType.REFORECAST".
    const properties = props.allProperties;
    const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");

    const [getCantCopy, {data: cantCopyData, loading: cantCopyLoading}] = useGetCrossCoaCopyCantCopyWithDriversLazyQuery({
        fetchPolicy: "no-cache"
    });

    const [copyDrivers, {loading: copyLoading}] = useCopyDriversCrossCoaMutation({
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true
    });

    const destinationData: CopyDriversCrossCoaPropertyInput = useMemo(() => {
        return {
            propertyId: props.destination.property.id,
            budgetYear: props.destination.budgetYear,
            version: props.destination.version,
        };
    }, [props.destination]);

    const sourceData: CopyDriversCrossCoaPropertyInput | null = useMemo(() => {
        if(!selectedPropertyId) {
            return null;
        }

        const property = properties.find(p => p.id === selectedPropertyId);
        if(!property) {
            return null;
        }

        return {
            propertyId: property.id,
            budgetYear: property.budgetYear,
            version: VersionType.Reforecast,
        };
    }, [selectedPropertyId, properties]);

    useEffect(() => {
        if(!sourceData) {
            return;
        }

        getCantCopy({
            variables: {
                source: sourceData,
                destination: destinationData
            }
        });
    }, [sourceData]);

    function doCopyDrivers() {
        if(!sourceData) {
            return;
        }

        copyDrivers({
            variables: {
                source: sourceData,
                destination: destinationData
            }
        }).then(res => {
            const data = res.data?.copyDriversCrossCoa;
            if(data) {
                if(!data.result) {
                    toast.error(data.resultString);
                } else {
                    toast.success("Drivers copied successfully!");
                    props.onModalClose();
                }
            }
        });
    }

    function renderAccountList(): ReactElement {
        if(!cantCopyData?.getCrossCoaCopyCantCopyWithDrivers) {
            return <></>;
        }
        if(cantCopyData.getCrossCoaCopyCantCopyWithDrivers.destinationLocked) {
            return <div>Destination property already has a copy in progress, please wait.</div>;
        }
        if(cantCopyData.getCrossCoaCopyCantCopyWithDrivers.missingAccounts.length === 0) {
            return <div>All Accounts will be copied.</div>;
        }
        return <ul>
            {cantCopyData.getCrossCoaCopyCantCopyWithDrivers.missingAccounts.map(a => {
                return <li key={`${a.accountNumber}${a.accountName}`}>{a.accountName}({a.accountNumber})</li>;
            })}
        </ul>;
    }

    return <Modal onClose={props.onModalClose}>
        <Header>
            {props.title ?? "Copy Modeling Methods"}
        </Header>
        <Body className={css.copyDriverCrossCoaModal}>
            <form>
                <h4 className={css.header}>Source Property</h4>
                <CustomZDDropdown
                        widthSpec={"100%"}
                        applySelectedItems={items => Array.isArray(items) ? setSelectedPropertyId(items[0]) : setSelectedPropertyId(items)}
                        openDropdownPlaceholder="Source Property"
                        closedDropdownPlaceholder="Source Property"
                        options={properties.filter(p => p.id !== props.destination.property.id).map(p => ({value: p.id, label: p.name}))}
                        isError={undefined}
                />
                <h4 className={css.header}>Destination Property</h4>
                <div>
                    {props.destination.property.name}
                </div>
            </form>
            <h4 className={css.header}>Accounts that Won't Copy</h4>
            <div className={css.headerDescription}>Different Charts of Accounts may not have the same accounts. If we can't match source accounts to the destination, a list that won't copy will appear below.</div>
            <div>
                {!selectedPropertyId ?
                        <div>Please select a destination property.</div> :
                        <div>
                            {cantCopyLoading ?
                                    <>Loading...</> :
                                    <>{renderAccountList()}</>
                            }
                        </div>
                }
            </div>
        </Body>
        <Footer className={css.copyDriverCrossCoaModalFooter}>
            <Button isBasic onClick={props.onModalClose}>Cancel</Button>
            <Button isPrimary disabled={!sourceData || copyLoading || cantCopyLoading} onClick={doCopyDrivers}>
                {!copyLoading ? "Copy" : <>Copying <Dots /></>}
            </Button>
        </Footer>
        <Close aria-label="Close modal" />
    </Modal>;
}

export interface CopyDriverCrossCoaModalProps {
    allProperties: Property[];
    source: {
        property: Property;
        budgetYear: number;
        version: VersionType.Reforecast | VersionType.Budget;
    }
    onModalClose: () => void;
}

export function CopyDriverCrossCoaModal(props: CopyDriverCrossCoaModalProps): ReactElement {
    // Right now this is only being used for acquisition properties, so I'm going to make some assumptions
    // that simplify the code that will need to changed if other types of properties are required.
    //  1. The destination property year and version will always be "property.reforecastYear" an "VersionType.REFORECAST".
    const properties = props.allProperties;
    const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");

    const [getCantCopy, {data: cantCopyData, loading: cantCopyLoading}] = useGetCrossCoaCopyCantCopyWithDriversLazyQuery({
        fetchPolicy: "no-cache"
    });

    const [copyDrivers, {loading: copyLoading}] = useCopyDriversCrossCoaMutation({
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true
    });

    const sourceData: CopyDriversCrossCoaPropertyInput = useMemo(() => {
        return {
            propertyId: props.source.property.id,
            budgetYear: props.source.budgetYear,
            version: props.source.version,
        };
    }, [props.source]);

    const destinationData: CopyDriversCrossCoaPropertyInput | null = useMemo(() => {
        if(!selectedPropertyId) {
            return null;
        }

        const property = properties.find(p => p.id === selectedPropertyId);
        if(!property) {
            return null;
        }

        return {
            propertyId: property.id,
            budgetYear: property.budgetYear,
            version: VersionType.Reforecast,
        };
    }, [selectedPropertyId, properties]);

    useEffect(() => {
        if(!destinationData) {
            return;
        }

        getCantCopy({
            variables: {
                source: sourceData,
                destination: destinationData
            }
        });
    }, [destinationData]);

    function doCopyDrivers() {
        if(!destinationData) {
            return;
        }

        copyDrivers({
            variables: {
                source: sourceData,
                destination: destinationData
            }
        }).then(res => {
            const data = res.data?.copyDriversCrossCoa;
            if(data) {
                if(!data.result) {
                    toast.error(data.resultString);
                } else {
                    toast.success("Drivers copied successfully!");
                    props.onModalClose();
                }
            }
        });
    }

    function renderAccountList(): ReactElement {
        if(!cantCopyData?.getCrossCoaCopyCantCopyWithDrivers) {
            return <></>;
        }
        if(cantCopyData.getCrossCoaCopyCantCopyWithDrivers.destinationLocked) {
            return <div>Destination property already has a copy in progress, please wait.</div>;
        }
        if(cantCopyData.getCrossCoaCopyCantCopyWithDrivers.missingAccounts.length === 0) {
            return <div>All Accounts will be copied.</div>;
        }
        return <ul>
            {cantCopyData.getCrossCoaCopyCantCopyWithDrivers.missingAccounts.map(a => {
                return <li key={`${a.accountNumber}${a.accountName}`}>{a.accountName}({a.accountNumber})</li>;
            })}
        </ul>;
    }

    return <Modal onClose={props.onModalClose}>
        <Header>
            Copy Modeling Methods
        </Header>
        <Body className={css.copyDriverCrossCoaModal}>
            <form>
                <h4 className={css.header}>Source Property</h4>
                <div>
                    {props.source.property.name}
                </div>
                <h4 className={css.header}>Destination Property</h4>
                <CustomZDDropdown
                    widthSpec={"100%"}
                    applySelectedItems={items => Array.isArray(items) ? setSelectedPropertyId(items[0]) : setSelectedPropertyId(items)}
                    openDropdownPlaceholder="Destination Property"
                    closedDropdownPlaceholder="Destination Property"
                    options={properties.filter(p => p.id !== props.source.property.id).map(p => ({value: p.id, label: p.name}))}
                    isError={undefined}
                />
            </form>
            <h4 className={css.header}>Accounts that Won't Copy</h4>
            <div className={css.headerDescription}>Different Charts of Accounts may not have the same accounts. If we can't match source accounts to the destination, a list that won't copy will appear below.</div>
            <div>
                {!selectedPropertyId ?
                    <div>Please select a destination property.</div> :
                    <div>
                        {cantCopyLoading ?
                            <>Loading...</> :
                            <>{renderAccountList()}</>
                        }
                    </div>
                }
            </div>
        </Body>
        <Footer className={css.copyDriverCrossCoaModalFooter}>
            <Button isBasic onClick={props.onModalClose}>Cancel</Button>
            <Button isPrimary disabled={!destinationData || copyLoading} onClick={doCopyDrivers}>
                {!copyLoading ? "Copy" : <>Copying <Dots /></>}
            </Button>
        </Footer>
        <Close aria-label="Close modal" />
    </Modal>;
}

export function CopyDriverCrossCoaButton(props: Omit<CopyDriverCrossCoaModalProps, "onModalClose">): ReactElement {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    return <div>
        <Button onClick={() => setIsOpen(true)}>Copy Modeling Methods</Button>
        {isOpen && <CopyDriverCrossCoaModal {...props} onModalClose={() => setIsOpen(false)} />}
    </div>;
}